/**
 * Custom Connector Store
 * Zustand store for managing custom connectors
 */

import { create } from 'zustand';
import type {
  CustomConnector,
  CreateConnectorInput,
  UpdateConnectorInput,
  ConnectorOperation,
  CreateOperationInput,
  UpdateOperationInput,
  ConnectorOAuthConfig,
  OAuthConfigInput,
  TestOperationInput,
  TestResult,
  OpenAPIImportResult,
} from './types';
import * as api from './api';

interface ConnectorState {
  // Connectors
  connectors: CustomConnector[];
  connectorsLoading: boolean;
  connectorsError: string | null;
  currentConnector: CustomConnector | null;

  // Operations
  operations: ConnectorOperation[];
  operationsLoading: boolean;
  selectedOperation: ConnectorOperation | null;

  // OAuth Config
  oauthConfig: ConnectorOAuthConfig | null;
  oauthLoading: boolean;

  // Testing
  testResult: TestResult | null;
  testing: boolean;

  // Actions - Connectors
  loadConnectors: (params?: { is_published?: boolean; is_public?: boolean }) => Promise<void>;
  loadConnector: (id: string) => Promise<CustomConnector>;
  createConnector: (input: CreateConnectorInput) => Promise<CustomConnector>;
  updateConnector: (id: string, input: UpdateConnectorInput) => Promise<CustomConnector>;
  deleteConnector: (id: string) => Promise<void>;
  publishConnector: (id: string) => Promise<void>;
  unpublishConnector: (id: string) => Promise<void>;
  setCurrentConnector: (connector: CustomConnector | null) => void;

  // Actions - Operations
  loadOperations: (connectorId: string) => Promise<void>;
  createOperation: (connectorId: string, input: CreateOperationInput) => Promise<ConnectorOperation>;
  updateOperation: (
    connectorId: string,
    operationId: string,
    input: UpdateOperationInput
  ) => Promise<ConnectorOperation>;
  deleteOperation: (connectorId: string, operationId: string) => Promise<void>;
  reorderOperations: (connectorId: string, operationIds: string[]) => Promise<void>;
  setSelectedOperation: (operation: ConnectorOperation | null) => void;

  // Actions - OAuth
  loadOAuthConfig: (connectorId: string) => Promise<void>;
  saveOAuthConfig: (connectorId: string, config: OAuthConfigInput) => Promise<void>;
  deleteOAuthConfig: (connectorId: string) => Promise<void>;

  // Actions - Testing
  testAuth: (connectorId: string, credentials: Record<string, string>) => Promise<TestResult>;
  testOperation: (
    connectorId: string,
    operationId: string,
    input: TestOperationInput
  ) => Promise<TestResult>;
  clearTestResult: () => void;

  // Actions - Import
  importOpenAPI: (connectorId: string, spec: string, format?: 'json' | 'yaml') => Promise<OpenAPIImportResult>;

  clearErrors: () => void;
}

export const useConnectorStore = create<ConnectorState>((set, get) => ({
  // Initial state
  connectors: [],
  connectorsLoading: false,
  connectorsError: null,
  currentConnector: null,

  operations: [],
  operationsLoading: false,
  selectedOperation: null,

  oauthConfig: null,
  oauthLoading: false,

  testResult: null,
  testing: false,

  // Connector Actions
  loadConnectors: async (params) => {
    set({ connectorsLoading: true, connectorsError: null });
    try {
      const response = await api.listConnectors(params);
      set({ connectors: response.data, connectorsLoading: false });
    } catch (error) {
      set({
        connectorsError: (error as Error).message,
        connectorsLoading: false,
      });
    }
  },

  loadConnector: async (id: string) => {
    try {
      const response = await api.getConnector(id);
      set({ currentConnector: response.data });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  createConnector: async (input: CreateConnectorInput) => {
    set({ connectorsLoading: true, connectorsError: null });
    try {
      const response = await api.createConnector(input);
      set((state) => ({
        connectors: [...state.connectors, response.data],
        currentConnector: response.data,
        connectorsLoading: false,
      }));
      return response.data;
    } catch (error) {
      set({
        connectorsError: (error as Error).message,
        connectorsLoading: false,
      });
      throw error;
    }
  },

  updateConnector: async (id: string, input: UpdateConnectorInput) => {
    set({ connectorsLoading: true, connectorsError: null });
    try {
      const response = await api.updateConnector(id, input);
      set((state) => ({
        connectors: state.connectors.map((c) => (c.id === id ? response.data : c)),
        currentConnector: state.currentConnector?.id === id ? response.data : state.currentConnector,
        connectorsLoading: false,
      }));
      return response.data;
    } catch (error) {
      set({
        connectorsError: (error as Error).message,
        connectorsLoading: false,
      });
      throw error;
    }
  },

  deleteConnector: async (id: string) => {
    set({ connectorsLoading: true, connectorsError: null });
    try {
      await api.deleteConnector(id);
      set((state) => ({
        connectors: state.connectors.filter((c) => c.id !== id),
        currentConnector: state.currentConnector?.id === id ? null : state.currentConnector,
        connectorsLoading: false,
      }));
    } catch (error) {
      set({
        connectorsError: (error as Error).message,
        connectorsLoading: false,
      });
      throw error;
    }
  },

  publishConnector: async (id: string) => {
    try {
      const response = await api.publishConnector(id);
      set((state) => ({
        connectors: state.connectors.map((c) => (c.id === id ? response.data : c)),
        currentConnector: state.currentConnector?.id === id ? response.data : state.currentConnector,
      }));
    } catch (error) {
      throw error;
    }
  },

  unpublishConnector: async (id: string) => {
    try {
      const response = await api.unpublishConnector(id);
      set((state) => ({
        connectors: state.connectors.map((c) => (c.id === id ? response.data : c)),
        currentConnector: state.currentConnector?.id === id ? response.data : state.currentConnector,
      }));
    } catch (error) {
      throw error;
    }
  },

  setCurrentConnector: (connector: CustomConnector | null) => {
    set({ currentConnector: connector, operations: [], selectedOperation: null });
  },

  // Operation Actions
  loadOperations: async (connectorId: string) => {
    set({ operationsLoading: true });
    try {
      const response = await api.listOperations(connectorId);
      set({ operations: response.data, operationsLoading: false });
    } catch (error) {
      set({ operationsLoading: false });
      throw error;
    }
  },

  createOperation: async (connectorId: string, input: CreateOperationInput) => {
    try {
      const response = await api.createOperation(connectorId, input);
      set((state) => ({
        operations: [...state.operations, response.data],
        selectedOperation: response.data,
      }));
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  updateOperation: async (connectorId: string, operationId: string, input: UpdateOperationInput) => {
    try {
      const response = await api.updateOperation(connectorId, operationId, input);
      set((state) => ({
        operations: state.operations.map((op) =>
          op.id === operationId ? response.data : op
        ),
        selectedOperation:
          state.selectedOperation?.id === operationId ? response.data : state.selectedOperation,
      }));
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  deleteOperation: async (connectorId: string, operationId: string) => {
    try {
      await api.deleteOperation(connectorId, operationId);
      set((state) => ({
        operations: state.operations.filter((op) => op.id !== operationId),
        selectedOperation:
          state.selectedOperation?.id === operationId ? null : state.selectedOperation,
      }));
    } catch (error) {
      throw error;
    }
  },

  reorderOperations: async (connectorId: string, operationIds: string[]) => {
    try {
      await api.reorderOperations(connectorId, operationIds);
      // Reload to get updated order
      get().loadOperations(connectorId);
    } catch (error) {
      throw error;
    }
  },

  setSelectedOperation: (operation: ConnectorOperation | null) => {
    set({ selectedOperation: operation });
  },

  // OAuth Actions
  loadOAuthConfig: async (connectorId: string) => {
    set({ oauthLoading: true });
    try {
      const response = await api.getOAuthConfig(connectorId);
      set({ oauthConfig: response.data, oauthLoading: false });
    } catch (error) {
      set({ oauthConfig: null, oauthLoading: false });
      // OAuth config might not exist, which is OK
    }
  },

  saveOAuthConfig: async (connectorId: string, config: OAuthConfigInput) => {
    set({ oauthLoading: true });
    try {
      const response = await api.saveOAuthConfig(connectorId, config);
      set({ oauthConfig: response.data, oauthLoading: false });
    } catch (error) {
      set({ oauthLoading: false });
      throw error;
    }
  },

  deleteOAuthConfig: async (connectorId: string) => {
    set({ oauthLoading: true });
    try {
      await api.deleteOAuthConfig(connectorId);
      set({ oauthConfig: null, oauthLoading: false });
    } catch (error) {
      set({ oauthLoading: false });
      throw error;
    }
  },

  // Testing Actions
  testAuth: async (connectorId: string, credentials: Record<string, string>) => {
    set({ testing: true, testResult: null });
    try {
      const response = await api.testAuth(connectorId, credentials);
      set({ testResult: response.data, testing: false });
      return response.data;
    } catch (error) {
      set({ testing: false });
      throw error;
    }
  },

  testOperation: async (connectorId: string, operationId: string, input: TestOperationInput) => {
    set({ testing: true, testResult: null });
    try {
      const response = await api.testOperation(connectorId, operationId, input);
      set({ testResult: response.data, testing: false });
      return response.data;
    } catch (error) {
      set({ testing: false });
      throw error;
    }
  },

  clearTestResult: () => {
    set({ testResult: null });
  },

  // Import Actions
  importOpenAPI: async (connectorId: string, spec: string, format: 'json' | 'yaml' = 'json') => {
    try {
      const response = await api.importOpenAPI(connectorId, spec, format);
      // Reload operations after import
      get().loadOperations(connectorId);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  clearErrors: () => {
    set({ connectorsError: null });
  },
}));

// Selectors
export const useConnectors = () => useConnectorStore((state) => state.connectors);
export const useCurrentConnector = () => useConnectorStore((state) => state.currentConnector);
export const useConnectorOperations = () => useConnectorStore((state) => state.operations);
export const useSelectedOperation = () => useConnectorStore((state) => state.selectedOperation);
export const useConnectorOAuthConfig = () => useConnectorStore((state) => state.oauthConfig);
export const useConnectorTestResult = () => useConnectorStore((state) => state.testResult);
